import { createContext } from 'preact';
import { useContext, useEffect, useState } from 'preact/hooks';
import CaasStorageKeys from '../constants/caas.storage.keys';
import { ConsoleEvent } from '../sdk/console.event';
import LoadingScreen from '../components/shared/LoadingScreen/LoadingScreen';
import CaasLocalStorageKeys from '../constants/caas.local.storage.keys';
import useConfig from './config.context';
import SettingsService from '../services/settings.service';

const OrganizationsContext = createContext({
  organizations: [],
  loading: false,
});

const ORGANIZATION_CACHE_TIME = 1000 * 60 * 60 * 24;

function OrganizationsProvider({ children }) {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const { config, updateStorage } = useConfig();

  useEffect(() => {
    const organizationsStorage =
      config[CaasStorageKeys.STORAGE][
        CaasLocalStorageKeys.ORGANIZATIONS_NAMES
      ] ?? [];

    if (organizationsStorage) {
      const lessThan24Hours = organizationsStorage.lastUpdate
        ? new Date(organizationsStorage.lastUpdate) >
          new Date(new Date() - ORGANIZATION_CACHE_TIME)
        : false;
      if (
        organizationsStorage.lastUpdate &&
        lessThan24Hours &&
        organizationsStorage.organizations?.length > 0
      ) {
        setOrganizations(organizationsStorage.organizations);
        setLoading(false);
        return;
      }
    }

    getOrganizationsNames();
  }, []);

  const updateOrganizationsStorage = newOrganizations => {
    if (Array.isArray(newOrganizations) && newOrganizations.length === 0) {
      return;
    }

    const organizationsStorageData = {
      lastUpdate: new Date().toISOString(),
      organizations: newOrganizations,
    };

    updateStorage(
      CaasLocalStorageKeys.ORGANIZATIONS_NAMES,
      organizationsStorageData,
    );
  };

  const getOrganizationsNames = async () => {
    setLoading(true);
    try {
      const response = await SettingsService.getOrganization();
      setOrganizations(response.data);
      updateOrganizationsStorage(response.data);
    } catch (e) {
      ConsoleEvent.log('Error occurred getting organizations');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <LoadingScreen />}
      {!loading && (
        <OrganizationsContext.Provider value={{ organizations, loading }}>
          {children}
        </OrganizationsContext.Provider>
      )}
    </>
  );
}

export { OrganizationsContext, OrganizationsProvider };

export default function useOrganizationsConfig() {
  const { organizations, loading } = useContext(OrganizationsContext);
  return { organizations, loading };
}
